<script lang="ts" setup>
import { SrcSetSizes } from '~/composables/useSrcSet'
import { ApiBlog } from '~~/server/api/blog'

/* eslint-disable vue/no-v-html */
const route = useRoute()
const { data: page, redirect } = await useFetchWithRedirect<ApiBlog>('/api/blog', { route }, {
    params: {
        id: route.params.id,
        t: route.query.t || ''
    },
})

if(redirect && !redirect?.error) {
    navigateTo(redirect.data.url, redirect.data.options)
}

const { $t, $formatTableHtml } = useNuxtApp()
const breadcrumbs = computed<BreadcrumbsLink[]>(() => {
    const data: BreadcrumbsLink[] = []
    if (route.name !== 'magazine-detail') {
        // temporary disabled: KP-1446
        // data.push({
        //     name: $t('O nás'),
        //     slug: useNamedRoutes({ name: 'about-profile' }),
        // })

        data.push({
            name: $t('Tlačové správy'),
            slug: useNamedRoutes({ name: 'about-profile', hash: '#pages' }),
        })

        return data
    }

    data.push({
        name: $t('Magazín'),
        slug: useNamedRoutes({ name: 'magazine' }),
    })
    if (page.value?.data.category) {
        data.push({
            name: page.value.data.category.title,
            slug: useNamedRoutes({
                name: 'blog-category-detail',
                params: {
                    id: page.value.data.category.slug,
                },
            }),
        })
    }
    return data
})
const featuredImageSrcSet = computed((): SrcSetSizes | undefined => {
    if (!page.value || page.value?.data.featured_images.length === 0) return
    return useSrcSetSizes(page.value?.data.featured_images[0]!.path)
})

useSchemaOrg([
    defineArticle({
        name: page.value?.data.title,
        articleBody: useClearHtmlTags(page.value?.data.content || ''),
        image: page.value?.data.featured_images[0]?.path?.['700x0-t']?.url || '',
    }),
])

if (route.params.category !== page.value?.data?.category?.slug)
    useCanonicalHead({
        params: {
            category: page.value?.data?.category?.slug || (route.params.category as string),
        },
    })

// Page meta
usePageMeta({
    meta: page.value?.data.meta,
    title: page.value?.data.title,
    og_type: 'article'

})
</script>
<template>
  <ContainerFluid v-if="page">
    <Breadcrumbs
      :current="page.data.title"
      :links="breadcrumbs"
      class="border-bottom pb-3 mb-4 mb-md-10"
    />
    <article class="mb-12 mb-md-40">
      <header
        class="row justify-content-center"
        data-cy="article_detail_title"
      >
        <div class="col-12 col-md-10">
          <Label
            v-if="page.data.category"
            class="mb-2 me-2"
            size="big"
            :label="page.data.category.title"
            background-color="var(--global-color-secondary)"
            text-color="var(--global-color-white)"
          />
          <h1 class="mb-4 mb-md-9">
            {{ page.data.title }}
          </h1>

          <ul
            class="d-flex align-items-center gap-4 gap-md-8 mb-12 fs-smaller flex-wrap"
          >
            <li>
              <ArticleHeaderDate
                v-if="page.data?.published_at"
                :date="(page.data?.published_at?.toString())"
              />
            </li>
            <li>
              <ArticleHeaderAuthor author="Kooperativa" />
            </li>
            <li>
              <ArticleHeaderReadingTime :text="page.data.content" />
            </li>
          </ul>
        </div>
      </header>
      <img
        v-if="featuredImageSrcSet"
        :srcset="featuredImageSrcSet.srcSet"
        sizes="(min-width: 1170px) 1170px, 100vw"
        alt=""
        width="1170"
        height="743"
        class="d-block w-100 img-fluid rounded mb-12"
        data-cy="article_detail_featured_img"
      >
      <!-- WCAG 2.4.3: DOM order matches visual order (share buttons on left, content on right) -->
      <div class="row">
        <div class="col-md-2 col-lg-2 order-2 order-md-1 mt-4 mt-md-0">
          <ShareButtons
            v-if="page.data.featured_images[0]"
            :thumbnail="
              page.data.featured_images[0]!.path['1050x0-t']?.url ?? ''
            "
            :title="page.data.title"
          />
        </div>
        <div
          class="col-md-10 col-lg-8 order-1 order-md-2"
          data-cy="article_detail_content"
        >
          <Typo type="ext">
            <div
              class="magazine-detail-content"
              v-html="$formatTableHtml(page.data.content)"
            />
          </Typo>
          <div class="d-flex flex-wrap gap-2 mt-6">
            <Label
              v-for="(category, index) in page.data
                .categories_other"
              :key="`mdc-${index}`"
              :label="category.title"
              background-color="var(--global-color-secondary)"
              text-color="var(--global-color-white)"
            />
          </div>
        </div>
      </div>
    </article>
    <aside
      v-if="page.data.related_posts.length"
      class="mb-12 mb-md-20"
    >
      <PagesBlogRelatedArticlesSlider
        heading-level="h2"
        :heading="$t('Podobné články')"
        :data="page.data.related_posts"
      />
    </aside>
    <div class="pt-12 pt-md-20 border-top">
      <LazyWidgetHelpdeskExt />
    </div>
  </ContainerFluid>
</template>
